import { useCallback, useEffect, useState } from "react";
import { API_BASE, api } from "@/lib/apiClient";
import { Button } from "@/components/ui/Button";
import { FormHint } from "@/pages/stammdaten/schnittstellen/FormHint";

type CredentialSource = "settings" | "environment" | null;
type StripeMode = "test" | "live" | null;

interface StripeSettings {
  configured: boolean;
  publishable_key: string | null;
  publishable_key_source: CredentialSource;
  secret_key_configured: boolean;
  secret_key_source: CredentialSource;
  webhook_secret_configured: boolean;
  webhook_secret_source: CredentialSource;
  mode: StripeMode;
}

interface TenantSettings {
  stripe: StripeSettings;
}

interface StripeConnectionTest {
  ok: boolean;
  account_id: string | null;
  charges_enabled: boolean | null;
  livemode: boolean | null;
}

interface StripeIntegrationPanelProps {
  canWrite: boolean;
}

const WEBHOOK_PATH = "/api/v1/public/shop/webhooks/stripe";

function sourceLabel(source: CredentialSource): string | null {
  if (source === "settings") return "in CampDesk hinterlegt";
  if (source === "environment") return "über die Server-Umgebung aktiv";
  return null;
}

export function StripeIntegrationPanel({ canWrite }: StripeIntegrationPanelProps) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [testMessage, setTestMessage] = useState<string | null>(null);

  const [publishableKey, setPublishableKey] = useState("");
  const [secretKey, setSecretKey] = useState("");
  const [webhookSecret, setWebhookSecret] = useState("");
  const [clearSecretKey, setClearSecretKey] = useState(false);
  const [clearWebhookSecret, setClearWebhookSecret] = useState(false);
  const [stored, setStored] = useState<StripeSettings | null>(null);

  const webhookUrl = `${API_BASE || (typeof window !== "undefined" ? window.location.origin : "")}${WEBHOOK_PATH}`;

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    api
      .get<TenantSettings>("/api/v1/settings")
      .then((settings) => {
        const stripe = settings.stripe;
        setStored(stripe);
        setPublishableKey(stripe?.publishable_key ?? "");
        setSecretKey("");
        setWebhookSecret("");
        setClearSecretKey(false);
        setClearWebhookSecret(false);
      })
      .catch((e: Error) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  async function onSave(e: React.FormEvent) {
    e.preventDefault();
    if (!canWrite) return;
    setSaving(true);
    setError(null);
    setSuccess(false);
    setTestMessage(null);
    try {
      await api.patch<TenantSettings>("/api/v1/settings/stripe", {
        publishable_key: publishableKey.trim() || null,
        ...(secretKey.trim() ? { secret_key: secretKey.trim() } : {}),
        clear_secret_key: clearSecretKey,
        ...(webhookSecret.trim() ? { webhook_secret: webhookSecret.trim() } : {}),
        clear_webhook_secret: clearWebhookSecret,
      });
      setSuccess(true);
      load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Stripe-Zugang konnte nicht gespeichert werden.");
    } finally {
      setSaving(false);
    }
  }

  async function onTest() {
    if (!canWrite) return;
    setTesting(true);
    setError(null);
    setTestMessage(null);
    try {
      const result = await api.post<StripeConnectionTest>("/api/v1/settings/stripe/test");
      const mode = result.livemode ? "Live" : "Test";
      const charges =
        result.charges_enabled === false ? " Zahlungen sind im Stripe-Konto noch nicht freigeschaltet." : "";
      setTestMessage(
        `Verbindung erfolgreich${result.account_id ? ` (${result.account_id}, ${mode})` : ""}.${charges}`,
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : "Stripe-Verbindungstest fehlgeschlagen.");
    } finally {
      setTesting(false);
    }
  }

  if (loading) {
    return <p className="text-sm text-label-secondary">Laden…</p>;
  }

  const secretStored = stored?.secret_key_source === "settings";
  const webhookStored = stored?.webhook_secret_source === "settings";

  return (
    <form onSubmit={(e) => void onSave(e)} className="space-y-5">
      <FormHint>
        Gäste-Shop und Comfort Check-in buchen über diesen Zugang. Ein auf dem Server gesetzter
        Schlüssel gilt nur, solange hier keiner gespeichert ist. Legen Sie im Stripe-Dashboard
        unter Entwickler einen Webhook auf{" "}
        <code className="text-xs">{webhookUrl}</code> an und abonnieren Sie das Ereignis{" "}
        <code className="text-xs">checkout.session.completed</code>. Secret Key und Webhook-Secret
        werden verschlüsselt gespeichert und danach nicht wieder angezeigt.
      </FormHint>

      {stored?.configured ? (
        <div className="rounded-xl border border-status-green/30 bg-status-green/5 px-4 py-3 text-sm text-label">
          <p>
            Secret Key {sourceLabel(stored.secret_key_source) ?? "aktiv"}
            {stored.mode === "test" ? " · Testmodus" : stored.mode === "live" ? " · Live" : ""}
          </p>
          <p className="mt-1 text-label-secondary">
            Webhook-Secret{" "}
            {stored.webhook_secret_configured
              ? (sourceLabel(stored.webhook_secret_source) ?? "aktiv")
              : "fehlt – Zahlungsabschlüsse ohne Rückkehr des Gastes bleiben offen"}
          </p>
          {stored.publishable_key_source === "environment" && !stored.publishable_key ? (
            <p className="mt-1 text-label-secondary">
              Publishable Key ist über die Server-Umgebung aktiv. Ein hier gespeicherter Key hat
              Vorrang.
            </p>
          ) : null}
        </div>
      ) : (
        <p className="text-sm text-label-secondary">
          Noch kein Stripe-Zugang hinterlegt. Ohne Secret Key sind Online-Zahlungen nicht verfügbar.
        </p>
      )}

      {canWrite ? (
        <fieldset className="space-y-4" disabled={!canWrite}>
          <label className="flex flex-col gap-1.5 text-sm">
            <span className="font-medium text-label">Publishable Key</span>
            <input
              className="eh-input max-w-xl font-mono text-xs"
              value={publishableKey}
              onChange={(e) => setPublishableKey(e.target.value)}
              placeholder="pk_live_… oder pk_test_…"
              autoComplete="off"
              spellCheck={false}
            />
          </label>

          <label className="flex flex-col gap-1.5 text-sm">
            <span className="font-medium text-label">
              {secretStored ? "Neuen Secret Key setzen" : "Secret Key"}
            </span>
            <input
              type="password"
              className="eh-input max-w-xl font-mono text-xs"
              value={secretKey}
              onChange={(e) => {
                setSecretKey(e.target.value);
                if (e.target.value.trim()) setClearSecretKey(false);
              }}
              placeholder="sk_live_… oder rk_live_…"
              autoComplete="off"
            />
          </label>
          {secretStored ? (
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                className="h-4 w-4"
                checked={clearSecretKey}
                onChange={(e) => {
                  setClearSecretKey(e.target.checked);
                  if (e.target.checked) setSecretKey("");
                }}
              />
              Gespeicherten Secret Key entfernen
            </label>
          ) : null}

          <label className="flex flex-col gap-1.5 text-sm">
            <span className="font-medium text-label">
              {webhookStored ? "Neues Webhook-Secret setzen" : "Webhook-Secret"}
            </span>
            <input
              type="password"
              className="eh-input max-w-xl font-mono text-xs"
              value={webhookSecret}
              onChange={(e) => {
                setWebhookSecret(e.target.value);
                if (e.target.value.trim()) setClearWebhookSecret(false);
              }}
              placeholder="whsec_…"
              autoComplete="off"
            />
          </label>
          {webhookStored ? (
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                className="h-4 w-4"
                checked={clearWebhookSecret}
                onChange={(e) => {
                  setClearWebhookSecret(e.target.checked);
                  if (e.target.checked) setWebhookSecret("");
                }}
              />
              Gespeichertes Webhook-Secret entfernen
            </label>
          ) : null}
        </fieldset>
      ) : null}

      {error ? <p className="text-sm text-status-red">{error}</p> : null}
      {success ? <p className="text-sm text-status-green">Stripe-Zugang gespeichert.</p> : null}
      {testMessage ? <p className="text-sm text-status-green">{testMessage}</p> : null}

      {canWrite ? (
        <div className="flex flex-wrap items-center justify-end gap-3 border-t border-separator pt-4">
          <Button
            type="button"
            variant="secondary"
            loading={testing}
            disabled={!stored?.configured || saving}
            onClick={() => void onTest()}
          >
            Verbindung prüfen
          </Button>
          <Button type="submit" loading={saving}>
            Speichern
          </Button>
        </div>
      ) : null}
    </form>
  );
}
